import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { ActivityDomainDtoIn } from '@/models/in/ActivityDomainDtoIn';
import { ActivityDomainDtoOut } from '@/models/out/ActivityDomainDtoOut';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';

const QUERY_URL = 'management/activity-domains';

interface EditOrCreateActivityDomainDialogProps {
  open: boolean;
  onClose: () => void;
  domainToEdit?: ActivityDomainDtoIn | null;
  onSuccess: () => void;
}

const EditOrCreateActivityDomainDialog = ({
  open,
  onClose,
  domainToEdit,
  onSuccess,
}: EditOrCreateActivityDomainDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!domainToEdit;

  useEffect(() => {
    if (open) {
      setName(domainToEdit?.name ?? '');
      setError(null);
    }
  }, [open, domainToEdit]);

  const handleClose = () => {
    setName('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('activityDomainsManagement.dialog.nameRequired'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const body: ActivityDomainDtoOut = { name: name.trim() };

    try {
      if (isEditing) {
        await putAuth<ActivityDomainDtoIn, object, ActivityDomainDtoOut>(
          `${QUERY_URL}/${domainToEdit!.id}`, headers, {}, body
        );
      } else {
        await postAuth<ActivityDomainDtoIn, ActivityDomainDtoOut>(QUERY_URL, headers, body);
      }
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('already exists')) {
        setError(t('activityDomainsManagement.dialog.duplicateError'));
      } else {
        setError(t(isEditing
          ? 'activityDomainsManagement.dialog.errorUpdating'
          : 'activityDomainsManagement.dialog.errorCreating'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={isEditing
        ? t('activityDomainsManagement.dialog.editTitle')
        : t('activityDomainsManagement.dialog.createTitle')
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="ad-name" className="body-sm-semibold text-(--font-secondary)">
            {t('activityDomainsManagement.name')}
          </label>
          <input
            id="ad-name"
            type="text"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            placeholder={t('activityDomainsManagement.dialog.namePlaceholder')}
          />
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateActivityDomainDialog;
